import React, {useEffect, useRef, useState} from 'react';
import {BottomSheet} from '../components/BottomSheet';
import {SatisfactionModal} from '../onboarding-v3/components';
import badgeIcon from '../projects/emailConfirmation-0424/assets/badgeIcon.svg';
import reminderBellIcon from '../projects/emailConfirmation-0424/assets/reminderBellIcon.svg';
import {SecondChanceOffer} from './secondChanceOfferPrices';
// Nyx Winback 1's background (winback_paywall_background@2x.png) split at the hill crests,
// so the hills can be anchored to the price block at any width (see the sections below).
import winbackSky from './assets/winback_sky.jpg';
import winbackHills from './assets/winback_hills.jpg';

// I21 — second-chance offer sheet, opened by the dismiss treatment (corner ✕ or idle
// timer; see useDismissTreatment). Ports the sell of Nyx's Winback 1 screen (the same
// 14-day → annual offer; `Winback1Screen.tsx`): app icon, offer countdown, "2 weeks on us."
// title, "$0 due today" and the billing line with the struck-through
// 7-day trial. The details rows, CTA and links reuse the underlying paywall's parts
// (TrialDetailsList icons, CustomPaymentRequestButton look) so the sheet reads as part of
// the page it covers. Copy is the Nyx strings (`screen.winback_paywall.*`), English only,
// as before.
//
// Rendered via the shared `BottomSheet`, which supplies the scrim, slide-up, black surface
// and 24px padding.

// Nyx Winback 1's single-use offer countdown (SingleUseTimer + countdownBox): 5 minutes
// from the first time the sheet opens, kept in localStorage so reopening the sheet or
// reloading doesn't restart it; once it runs out the box says the offer was saved.
const COUNTDOWN_MS = 5 * 60 * 1000;
const COUNTDOWN_STORAGE_KEY = 'i21_offer_countdown_ends_at';
const YELLOW_1 = '#FFB800';

const readCountdownEnd = (): number | null => {
  try {
    const v = Number(window.localStorage.getItem(COUNTDOWN_STORAGE_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
};

const useOfferCountdown = (isOpen: boolean): number | null => {
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // The deadline this page already showed, so reopening can't restart the countdown even
  // when storage is unavailable.
  const endsAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    let end = readCountdownEnd() ?? endsAtRef.current;
    if (end === null) {
      end = Date.now() + COUNTDOWN_MS;
      try {
        window.localStorage.setItem(COUNTDOWN_STORAGE_KEY, String(end));
      } catch {
        // Storage unavailable (private mode): the countdown still runs for this page.
      }
    }
    endsAtRef.current = end;
    setEndsAt(end);
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isOpen]);
  return endsAt === null ? null : Math.max(0, endsAt - now);
};

const formatCountdown = (ms: number) => {
  const total = Math.ceil(ms / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

// The colours at the top and bottom of the Winback 1 art, filling past the images when a
// section is taller than its image (narrow screens, card form open).
const SKY_TOP = '#2a1452';
const HILLS_BOTTOM = '#100a20';
// RISE signature gradient, used here as the sheet's edge.
const BRAND_GRADIENT = 'linear-gradient(135deg, #721CFF, #FF4A8B, #FD8206)';

type SecondChanceOfferSheetProps = {
  isOpen: boolean;
  offer: SecondChanceOffer;
  /** Trial length of the plan the user had selected; the struck-through "7" only makes
   * sense when it is shorter than the offer's (not for the 30-day $0.99 plan). */
  originalDaysFree: number;
  /** "Apple Pay" / "Google Pay", for the line under the CTA. Only set when a wallet is available. */
  paymentMethod?: string;
  /** Apple/Google Pay is available: REDEEM opens the wallet, and the card form sits behind
   * "Or pay with a card". Without a wallet the card form shows straight away. */
  walletAvailable: boolean;
  /** Email (when needed) + Stripe card field + error, rendered by PaymentIntentScreen so it
   * shares its Elements instance and checkout state. */
  cardForm: React.ReactNode;
  /** The card form is complete enough to submit (e.g. a valid email when one is asked for). */
  canSubmitCard: boolean;
  processing: boolean;
  /** REDEEM with a wallet. */
  onAccept: () => void;
  /** REDEEM with the card form. */
  onSubmitCard: () => void;
  onDecline: () => void;
};

const CTA_STYLE: React.CSSProperties = {
  width: '100%',
  height: 48,
  borderRadius: 12,
  border: '1px solid #e0e0e0',
  background: '#fff',
  color: '#333',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
};

const SecondChanceOfferSheet: React.FC<SecondChanceOfferSheetProps> = ({
  isOpen,
  offer,
  originalDaysFree,
  paymentMethod,
  walletAvailable,
  cardForm,
  canSubmitCard,
  processing,
  onAccept,
  onSubmitCard,
  onDecline,
}) => {
  const [showGuarantee, setShowGuarantee] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  // Each time the sheet opens, wallet users start on the wallet button again.
  useEffect(() => {
    if (!isOpen) setCardOpen(false);
  }, [isOpen]);
  const showCard = !walletAvailable || cardOpen;
  const remainingMs = useOfferCountdown(isOpen);
  const extendsTrial = originalDaysFree > 0 && originalDaysFree < offer.daysFree;

  return (
    <BottomSheet isOpen={isOpen} onClose={onDecline}>
      {showGuarantee ? <SatisfactionModal onClose={() => setShowGuarantee(false)} /> : null}
      {/* 2px brand-gradient border on the top and sides, following the rounded top corners
          (padding-box fill over a border-box gradient), bled to the BottomSheet's edges with
          negative margins. Inside it, Winback 1's sky sits behind the headline and its hills
          start exactly where the price block starts, whatever the width or text wrapping. */}
      <div
        className="text-white"
        style={{
          margin: -24,
          border: '2px solid transparent',
          borderBottom: 0,
          borderRadius: '24px 24px 0 0',
          background: `linear-gradient(${HILLS_BOTTOM}, ${HILLS_BOTTOM}) padding-box, ${BRAND_GRADIENT} border-box`,
        }}
      >
        <div
          style={{
            padding: '24px 12px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '22px 22px 0 0',
            background: `url(${winbackSky}) center bottom / cover no-repeat, ${SKY_TOP}`,
          }}
        >
          <img src="/app-icon.png" alt="RISE" style={{width: 52, height: 51}} />
          {remainingMs !== null ? (
            <div
              role="timer"
              style={{
                margin: '12px 0',
                minWidth: 210,
                maxWidth: 240,
                padding: '4px 8px',
                borderRadius: 8,
                background: YELLOW_1,
                color: '#000',
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'center',
                textTransform: 'uppercase',
              }}
            >
              {remainingMs > 0 ? `Offer ends in ${formatCountdown(remainingMs)}` : 'We saved your offer for 24hrs'}
            </div>
          ) : null}
          <h3 style={{margin: 0, fontSize: 38, lineHeight: '44px', fontWeight: 600, textAlign: 'center'}}>
            2 weeks on us.
          </h3>
          <p style={{margin: '4px 0 0', fontSize: 16, lineHeight: '19px', fontWeight: 500, textAlign: 'center'}}>
            Receive an extended 2 week free trial
          </p>
        </div>

        <div
          style={{
            // The crests scale with the width, so the price clears them on tablets too.
            padding: 'max(44px, 11.5vw) 12px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: `url(${winbackHills}) center top / 100% auto no-repeat, ${HILLS_BOTTOM}`,
          }}
        >
          <div style={{display: 'flex', alignItems: 'baseline', gap: 8}}>
            <span style={{fontSize: 48, lineHeight: '52px', fontWeight: 700}}>{offer.dueToday}</span>
            <span style={{fontSize: 18, lineHeight: '22px', textTransform: 'uppercase'}}>Due today</span>
          </div>
          <p style={{margin: '6px 0 0', fontSize: 14, lineHeight: '20px', textAlign: 'center'}}>
            {extendsTrial ? (
              <>
                <s>{originalDaysFree}</s>{' '}
              </>
            ) : null}
            {offer.daysFree} day FREE trial THEN <b>{offer.annualPrice}</b>/year
            <br />
            (that's just {offer.monthlyPrice}/mo)
          </p>

          <ul
            style={{
              margin: '16px 0 0',
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <li style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 14}}>
              <img src={reminderBellIcon} alt="" />
              <span>Reminder before your trial ends</span>
            </li>
            <li style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 14}}>
              <img src={badgeIcon} alt="" style={{margin: '-5px 0'}} />
              <span>
                Cancel anytime and{' '}
                <button
                  type="button"
                  onClick={() => setShowGuarantee(true)}
                  style={{
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    textDecoration: 'underline',
                  }}
                >
                  money back guarantee
                </button>
              </span>
            </li>
          </ul>

          <div
            style={{
              alignSelf: 'stretch',
              padding: '18px 12px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {showCard ? (
              <form
                style={{width: '100%', display: 'flex', flexDirection: 'column', gap: 8}}
                onSubmit={e => {
                  e.preventDefault();
                  if (canSubmitCard && !processing) onSubmitCard();
                }}
              >
                {cardForm}
                {/* Same look as the paywall's CustomPaymentRequestButton CTA. */}
                <button
                  type="submit"
                  disabled={!canSubmitCard || processing}
                  style={{...CTA_STYLE, opacity: !canSubmitCard || processing ? 0.5 : 1}}
                >
                  {processing ? 'Processing…' : 'REDEEM 2 WEEKS FREE'}
                </button>
              </form>
            ) : (
              <button type="button" onClick={onAccept} disabled={processing} style={CTA_STYLE}>
                REDEEM 2 WEEKS FREE
              </button>
            )}
            {walletAvailable && paymentMethod && !showCard ? (
              <p className="text-xs" style={{margin: 0, textAlign: 'center'}}>
                Start now securely using {paymentMethod}.
              </p>
            ) : null}
            {walletAvailable ? (
              // Locked while a card payment is in flight so a wallet payment can't start too.
              <button
                type="button"
                onClick={() => setCardOpen(!cardOpen)}
                disabled={processing}
                className="text-xs underline"
              >
                {cardOpen ? `Use ${paymentMethod ?? 'Apple Pay'} instead` : 'Or pay with a card'}
              </button>
            ) : null}
            <button type="button" onClick={onDecline} className="text-xs underline" style={{padding: '4px 8px'}}>
              No thanks
            </button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
};

export default SecondChanceOfferSheet;
